// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { formatAnchorDate, formatDays } from '@/billing/lifecycle/date-helpers';
import type { SegmentKey } from '@/billing/lifecycle/segments';
import type { LifecycleRow } from '@/billing/lifecycle/transforms';
import { DateTime } from 'luxon';

const EM_DASH = '—';

// The Gantt axis is UTC calendar-day space: date-only anchors become
// UTC civil midnights, while timestamp anchors keep their actual instant.
function dateOnlyAxisDate(value: DateTime | null): DateTime | null {
  if (value === null) return null;
  return DateTime.utc(value.year, value.month, value.day);
}

function timestampAxisDate(value: DateTime | null): DateTime | null {
  return value?.toUTC() ?? null;
}

function formatAxisDate(value: DateTime): string {
  return value.setZone('utc').setLocale('en-US').toLocaleString({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// One row in the per-invoice Gantt — a segment's start/end days on the
// invoice's axis plus the metadata needed for tooltip and hover-marker
// rendering. `startDay === null || endDay === null` → the row isn't
// placeable (e.g. invoice not yet sent for a delivery-lag row) and the
// layout drops it entirely rather than rendering an empty track.
export interface PerInvoiceAxisRow {
  key: SegmentKey;
  startDay: number | null;
  endDay: number | null;
  displayValue: string;
  startLabel: string | null;
  endLabel: string | null;
  // Formatted calendar dates for the segment's chain-start and chain-end
  // anchors — used by the tooltip "Start: …" / "End: …" lines. Null when
  // the anchor date itself is missing.
  startDate: string | null;
  endDate: string | null;
}

// Raw axis bounds in the invoice's day-offset space. Null when the
// invoice has no renderable rows yet (e.g. no billing_eligible_date).
export interface PerInvoiceAxisBounds {
  axisMin: number;
  axisMax: number;
}

interface PerInvoiceChart {
  axisRows: PerInvoiceAxisRow[];
  axisBounds: PerInvoiceAxisBounds;
  // Formatted calendar dates at the axis endpoints — rendered below the
  // chart as a range indicator.
  leftmostDate: string;
  rightmostDate: string;
  // Day-offset of "today" if it falls within the axis range; null
  // otherwise. Used by the per-invoice Gantt to draw a dotted vertical
  // "today" marker when the timeline intersects the current date.
  todayDay: number | null;
}

// Build the per-invoice Gantt's row specs + raw axis bounds from a
// LifecycleRow. Returns null when the invoice has no chartable data —
// typically when `billing_eligible_date` is missing so there's no x=0
// anchor for the chart, or when no row is fully placeable yet.
//
// The row order follows the chain: chargeLag → invoiceReview →
// deliveryLag → dateLag → actualTerms → delinquency. Rows whose
// start/end anchors aren't both resolved (e.g. delivery lag for an
// unsent invoice) are dropped entirely — the layout shows one bar per
// segment that has both anchors, no placeholder tracks.
export function buildPerInvoiceChart(
  row: LifecycleRow
): PerInvoiceChart | null {
  const axisStart = dateOnlyAxisDate(row.billingEligibleDate);
  if (axisStart === null) {
    return null;
  }

  const drafted = timestampAxisDate(row.draftedAt);
  const opened = timestampAxisDate(row.openedAt);
  const sent = timestampAxisDate(row.firstSentAt);
  const invoiceDate = dateOnlyAxisDate(row.invoiceDate);
  const due = dateOnlyAxisDate(row.dueDate);
  const paid = dateOnlyAxisDate(row.paidDate);

  const dayFrom = (value: DateTime | null): number | null => {
    if (value === null) return null;
    return value.diff(axisStart, 'days').days;
  };

  const eligibleDay = 0;
  const draftedDay = dayFrom(drafted);
  const openedDay = dayFrom(opened);
  const sentDay = dayFrom(sent);
  const invoiceDateDay = dayFrom(invoiceDate);
  const dueDay = dayFrom(due);
  const paidDay = dayFrom(paid);

  // Fall back to "today" as the right edge for unpaid invoices so the
  // delinquency bar can render up to the current date. For paid
  // invoices we deliberately do NOT include nowDay — otherwise the axis
  // would stretch past paid_date by whatever time has passed since
  // payment, leaving dead space on the right edge of the chart.
  const nowDay = DateTime.now().toUTC().diff(axisStart, 'days').days;
  const delinquencyRightEdge = paidDay ?? nowDay;

  const eligibleLabel = formatAnchorDate(row.billingEligibleDate);
  const draftedLabel = formatAnchorDate(row.draftedAt);
  const openedLabel = formatAnchorDate(row.openedAt);
  const sentLabel = formatAnchorDate(row.firstSentAt);
  const invoiceDateLabel = formatAnchorDate(row.invoiceDate);
  const dueLabel = formatAnchorDate(row.dueDate);
  const paidLabel = formatAnchorDate(row.paidDate);
  const todayLabel = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const delinquencyBottomLabel =
    paidLabel !== null ? `Paid: ${paidLabel}` : `Today: ${todayLabel}`;

  const allRows: PerInvoiceAxisRow[] = [
    {
      key: 'chargeLag',
      startDay: eligibleDay,
      endDay: draftedDay,
      displayValue:
        row.chargeLag !== null ? formatDays(row.chargeLag) : EM_DASH,
      startLabel: eligibleLabel !== null ? `Eligible: ${eligibleLabel}` : null,
      endLabel: draftedLabel !== null ? `Drafted: ${draftedLabel}` : null,
      startDate: eligibleLabel,
      endDate: draftedLabel,
    },
    {
      key: 'invoiceReview',
      startDay: draftedDay,
      endDay: openedDay,
      displayValue:
        row.invoiceReview !== null ? formatDays(row.invoiceReview) : EM_DASH,
      startLabel: draftedLabel !== null ? `Drafted: ${draftedLabel}` : null,
      endLabel: openedLabel !== null ? `Opened: ${openedLabel}` : null,
      startDate: draftedLabel,
      endDate: openedLabel,
    },
    {
      key: 'deliveryLag',
      startDay: openedDay,
      endDay: sentDay,
      displayValue:
        row.deliveryLag !== null ? formatDays(row.deliveryLag) : EM_DASH,
      startLabel: openedLabel !== null ? `Opened: ${openedLabel}` : null,
      endLabel: sentLabel !== null ? `Sent: ${sentLabel}` : null,
      startDate: openedLabel,
      endDate: sentLabel,
    },
    {
      key: 'dateLag',
      startDay: sentDay,
      endDay: invoiceDateDay,
      displayValue:
        row.dateLag !== null ? `${row.dateLag.toString()}d` : EM_DASH,
      startLabel: sentLabel !== null ? `Sent: ${sentLabel}` : null,
      endLabel:
        invoiceDateLabel !== null ? `Invoice date: ${invoiceDateLabel}` : null,
      startDate: sentLabel,
      endDate: invoiceDateLabel,
    },
    {
      // Actual terms is backend-gated on the invoice being OPEN or
      // later (opened_at locks the printed terms in). DRAFT invoices
      // may have invoice_date + due_date already populated from the
      // contract, but we intentionally don't draw a bar in that case —
      // the value isn't finalized until the invoice opens.
      key: 'actualTerms',
      startDay: row.actualTerms !== null ? invoiceDateDay : null,
      endDay: row.actualTerms !== null ? dueDay : null,
      displayValue:
        row.actualTerms !== null ? `${row.actualTerms.toString()}d` : EM_DASH,
      startLabel:
        invoiceDateLabel !== null ? `Invoice date: ${invoiceDateLabel}` : null,
      endLabel: dueLabel !== null ? `Due: ${dueLabel}` : null,
      startDate: invoiceDateLabel,
      endDate: dueLabel,
    },
    {
      // Delinquency only renders when the backend has actually assigned
      // a value — i.e. the invoice is paid or unpaid-past-due. A
      // not-yet-due open invoice has row.delinquency === null; showing
      // a bar from due → now would misleadingly suggest delinquency.
      key: 'delinquency',
      startDay: row.delinquency !== null ? dueDay : null,
      endDay: row.delinquency !== null ? delinquencyRightEdge : null,
      displayValue:
        row.delinquency !== null ? `${row.delinquency.toString()}d` : EM_DASH,
      startLabel: dueLabel !== null ? `Due: ${dueLabel}` : null,
      endLabel: delinquencyBottomLabel,
      startDate: dueLabel,
      endDate: paidLabel ?? todayLabel,
    },
  ];

  // Drop rows whose bar can't be placed — no blank tracks. If nothing
  // is placeable there's no chart at all.
  const axisRows = allRows.filter(
    (r) => r.startDay !== null && r.endDay !== null
  );
  if (axisRows.length === 0) {
    return null;
  }

  const renderedDayValues: number[] = [];
  for (const r of axisRows) {
    if (r.startDay !== null && r.endDay !== null) {
      renderedDayValues.push(r.startDay, r.endDay);
    }
  }
  const axisMin = Math.min(...renderedDayValues);
  const axisMax = Math.max(...renderedDayValues);

  const leftmostDate = formatAxisDate(axisStart.plus({ days: axisMin }));
  const rightmostDate = formatAxisDate(axisStart.plus({ days: axisMax }));

  const todayDay = nowDay >= axisMin && nowDay <= axisMax ? nowDay : null;

  return {
    axisRows,
    axisBounds: { axisMin, axisMax },
    leftmostDate,
    rightmostDate,
    todayDay,
  };
}
